import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PublicIcon from "@mui/icons-material/Public";
import TodayIcon from "@mui/icons-material/Today";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";

const ChatIPAddressesSection = () => {
  const [ipAddresses, setIpAddresses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filterBanned, setFilterBanned] = useState("all");
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [selectedIP, setSelectedIP] = useState(null);
  const [selectedIPDetails, setSelectedIPDetails] = useState(null);
  const [banReason, setBanReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchIPAddresses();
    fetchStats();
  }, [page, filterBanned]);

  const fetchIPAddresses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "50",
      });
      if (search) params.append("search", search);
      if (filterBanned !== "all") params.append("banned", filterBanned);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/ip-addresses?${params}`
      );
      setIpAddresses(response.data.ipAddresses);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
    } catch (err) {
      console.error("Failed to fetch IP addresses:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/ip-addresses/stats`
      );
      setStats(response.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchIPAddresses();
  };

  const handleViewDetails = async (ip) => {
    setSelectedIP(ip);
    setDetailDialogOpen(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/ip-addresses/${ip.id}`
      );
      setSelectedIPDetails(response.data);
    } catch (err) {
      console.error("Failed to fetch IP details:", err);
    }
  };

  const handleOpenBanDialog = (ip) => {
    setSelectedIP(ip);
    setBanReason(ip.ban_reason || "");
    setBanDialogOpen(true);
  };

  const handleBanToggle = async () => {
    if (!selectedIP) return;
    setSaving(true);

    try {
      const newBanStatus = !selectedIP.is_banned;
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/ip-addresses/${selectedIP.id}/ban`,
        {
          is_banned: newBanStatus,
          ban_reason: newBanStatus ? banReason : null,
        }
      );
      fetchIPAddresses();
      fetchStats();
      setBanDialogOpen(false);
    } catch (err) {
      console.error("Failed to update ban status:", err);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const getCountryFlag = (countryCode) => {
    if (!countryCode || countryCode === "LO") return null;
    // Convert country code to flag emoji
    const codePoints = countryCode
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  return (
    <Box>
      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
              <PublicIcon sx={{ fontSize: 32, color: "#2196f3", mb: 1 }} />
              <Typography variant="h4" sx={{ color: "#2196f3", fontWeight: 700 }}>{stats.total}</Typography>
              <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Total IPs</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
              <BlockIcon sx={{ fontSize: 32, color: "#f44336", mb: 1 }} />
              <Typography variant="h4" sx={{ color: "#f44336", fontWeight: 700 }}>{stats.banned}</Typography>
              <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Banned IPs</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
              <TodayIcon sx={{ fontSize: 32, color: "#4caf50", mb: 1 }} />
              <Typography variant="h4" sx={{ color: "#4caf50", fontWeight: 700 }}>{stats.seenToday}</Typography>
              <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Active Today</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
              <PersonIcon sx={{ fontSize: 32, color: "#ff9800", mb: 1 }} />
              <Typography variant="h4" sx={{ color: "#ff9800", fontWeight: 700 }}>{stats.newToday}</Typography>
              <Typography variant="caption" sx={{ color: "#9e9e9e" }}>New Today</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Top Countries */}
      {stats?.byCountry?.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867" }}>
          <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 1 }}>Top Countries</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {stats.byCountry.map((country) => (
              <Chip
                key={country.country_code}
                label={`${getCountryFlag(country.country_code) || ""} ${country.country} (${country.count})`}
                size="small"
                sx={{ backgroundColor: "#1a1a2e", color: "#fff" }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867" }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <form onSubmit={handleSearch} style={{ display: "flex", gap: 8, flex: 1, minWidth: 200 }}>
            <TextField
              size="small"
              placeholder="Search IP, city, country, ISP..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": { borderColor: "#394867" },
                  "&:hover fieldset": { borderColor: "#541b14" },
                },
              }}
            />
            <Button type="submit" variant="contained" sx={{ backgroundColor: "#541b14" }}>
              <SearchIcon />
            </Button>
          </form>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: "#9e9e9e" }}>Status</InputLabel>
            <Select
              value={filterBanned}
              label="Status"
              onChange={(e) => { setFilterBanned(e.target.value); setPage(1); }}
              sx={{
                color: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
                "& .MuiSvgIcon-root": { color: "#9e9e9e" },
              }}
            >
              <MenuItem value="all">All IPs</MenuItem>
              <MenuItem value="true">Banned Only</MenuItem>
              <MenuItem value="false">Active Only</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={() => { fetchIPAddresses(); fetchStats(); }} sx={{ color: "#9e9e9e" }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* IP Addresses Table */}
      <Paper sx={{ backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: "#fff" }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#0f1729" }}>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>IP Address</TableCell>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Location</TableCell>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>ISP</TableCell>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Guests</TableCell>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Last Seen</TableCell>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Status</TableCell>
                    <TableCell align="right" sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ipAddresses.map((ip) => (
                    <TableRow key={ip.id} hover sx={{ "&:hover": { backgroundColor: "#1a1a2e" }, opacity: ip.is_banned ? 0.6 : 1 }}>
                      <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                        <Typography
                          component={Link}
                          to={`/admin/chatroom/ip/${encodeURIComponent(ip.ip_address)}/users`}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            color: "#2196f3",
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {ip.ip_address}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>
                        {ip.city && ip.country ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <span>{getCountryFlag(ip.country_code)}</span>
                            <span>{ip.city}, {ip.country}</span>
                          </Box>
                        ) : (
                          <span style={{ color: "#666" }}>Unknown</span>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ip.isp || "-"}
                      </TableCell>
                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>
                        {ip.guest_count || 0}
                      </TableCell>
                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>
                        <Typography variant="caption">{formatDate(ip.last_seen_at)}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                        {ip.is_banned ? (
                          <Chip label="Banned" size="small" sx={{ backgroundColor: "#4a1515", color: "#ef5350" }} />
                        ) : (
                          <Chip label="Active" size="small" sx={{ backgroundColor: "#1b3a1b", color: "#4caf50" }} />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: "1px solid #394867" }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewDetails(ip)} sx={{ color: "#2196f3" }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={ip.is_banned ? "Unban IP" : "Ban IP"}>
                          <IconButton size="small" onClick={() => handleOpenBanDialog(ip)} sx={{ color: ip.is_banned ? "#4caf50" : "#f44336" }}>
                            {ip.is_banned ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  sx={{
                    "& .MuiPaginationItem-root": { color: "#9e9e9e" },
                    "& .Mui-selected": { backgroundColor: "#541b14 !important", color: "#fff" },
                  }}
                />
              </Box>
            )}

            {ipAddresses.length === 0 && (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography sx={{ color: "#666" }}>No IP addresses found</Typography>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Details Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { backgroundColor: "#16213e", color: "#fff" } }}>
        <DialogTitle>
          IP Address Details
          {selectedIP?.is_banned && <Chip label="Banned" size="small" sx={{ ml: 2, backgroundColor: "#4a1515", color: "#ef5350" }} />}
        </DialogTitle>
        <DialogContent>
          {selectedIPDetails ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: "#1a1a2e", borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 1 }}>IP Information</Typography>
                  <Typography sx={{ color: "#fff", fontFamily: "monospace", fontSize: "1.2rem" }}>{selectedIPDetails.ipAddress.ip_address}</Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                      <strong>Location:</strong> {selectedIPDetails.ipAddress.city}, {selectedIPDetails.ipAddress.region}, {selectedIPDetails.ipAddress.country}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                      <strong>ISP:</strong> {selectedIPDetails.ipAddress.isp || "Unknown"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                      <strong>Timezone:</strong> {selectedIPDetails.ipAddress.timezone || "Unknown"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                      <strong>Hostname:</strong> {selectedIPDetails.ipAddress.hostname || "Unknown"}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: "#1a1a2e", borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 1 }}>Activity</Typography>
                  <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                    <strong>First Seen:</strong> {formatDate(selectedIPDetails.ipAddress.first_seen_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                    <strong>Last Seen:</strong> {formatDate(selectedIPDetails.ipAddress.last_seen_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                    <strong>Request Count:</strong> {selectedIPDetails.ipAddress.request_count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                    <strong>Guest Count:</strong> {selectedIPDetails.ipAddress.guest_count}
                  </Typography>
                  {selectedIPDetails.ipAddress.is_banned && (
                    <Box sx={{ mt: 2, p: 1, backgroundColor: "#4a1515", borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: "#ef5350" }}>
                        <strong>Banned by:</strong> {selectedIPDetails.ipAddress.banned_by}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#ef5350" }}>
                        <strong>Reason:</strong> {selectedIPDetails.ipAddress.ban_reason || "No reason provided"}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
              {selectedIPDetails.guests?.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: "#1a1a2e", borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 1 }}>Associated Guests ({selectedIPDetails.guests.length})</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867" }}>Username</TableCell>
                            <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867" }}>Gender</TableCell>
                            <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867" }}>Messages</TableCell>
                            <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867" }}>Status</TableCell>
                            <TableCell sx={{ color: "#666", borderBottom: "1px solid #394867" }}>Last Seen</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedIPDetails.guests.map((guest) => (
                            <TableRow key={guest.id}>
                              <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867" }}>{guest.username}</TableCell>
                              <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>{guest.gender}</TableCell>
                              <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>{guest.message_count}</TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                                {guest.is_banned ? (
                                  <Chip label="Banned" size="small" sx={{ height: 20, backgroundColor: "#4a1515", color: "#ef5350" }} />
                                ) : (
                                  <Chip label="Active" size="small" sx={{ height: 20, backgroundColor: "#1b3a1b", color: "#4caf50" }} />
                                )}
                              </TableCell>
                              <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>
                                <Typography variant="caption">{formatDate(guest.last_seen_at)}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              )}
            </Grid>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: "#fff" }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)} sx={{ color: "#9e9e9e" }}>Close</Button>
          <Button onClick={() => { setDetailDialogOpen(false); handleOpenBanDialog(selectedIP); }} sx={{ color: selectedIP?.is_banned ? "#4caf50" : "#f44336" }}>
            {selectedIP?.is_banned ? "Unban IP" : "Ban IP"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ban Dialog */}
      <Dialog open={banDialogOpen} onClose={() => setBanDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: "#16213e", color: "#fff" } }}>
        <DialogTitle>{selectedIP?.is_banned ? "Unban IP Address" : "Ban IP Address"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#9e9e9e", mb: 2 }}>
            {selectedIP?.is_banned
              ? `Are you sure you want to unban ${selectedIP?.ip_address}?`
              : `You are about to ban ${selectedIP?.ip_address}. All requests from this IP will be blocked.`}
          </Typography>
          {!selectedIP?.is_banned && (
            <TextField
              label="Ban Reason (optional)"
              fullWidth
              multiline
              rows={2}
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": { borderColor: "#394867" },
                  "&:hover fieldset": { borderColor: "#541b14" },
                },
                "& .MuiInputLabel-root": { color: "#9e9e9e" },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanDialogOpen(false)} sx={{ color: "#9e9e9e" }}>Cancel</Button>
          <Button
            onClick={handleBanToggle}
            disabled={saving}
            sx={{ color: selectedIP?.is_banned ? "#4caf50" : "#f44336" }}
          >
            {saving ? <CircularProgress size={20} /> : (selectedIP?.is_banned ? "Unban" : "Ban")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatIPAddressesSection;
